// v3 `stores/integrationStore.ts:58-113` (`channelPermissionMappings`) — 39 display rows, moved out
// of the store per contract §3.
//
// This map is also the ticket's decision point for the sub-entity gap. `meta_catalog`, `meta_pixel`,
// `tiktok_pixel` and `tiktok_catalog` have display metadata and NO `INTEGRATIONS_CONFIGS` entry:
// they are registered from inside the Meta/TikTok modals. The alternative shape — `subEntities` on
// `IntegrationProvider` — would invent a hierarchy the wire does not have (permissions come back as
// flat channel keys) and would duplicate these four fields. So they are rows here, tagged
// `subEntityOf`. `meta_cpas_account` is a fifth such row: v3 deleted its provider entry
// (Orphex-New-Front `e2e0c90e`) but kept the permission row, and it has no parent modal.
//
// Two deliberate corrections to the v3 data, both in lookup keys that resolve to nothing today:
//   · The Shopify row was keyed `shopify_account` while its own `key` array said `['shopify_store']`
//     and every other row is keyed by the provider id — so `getPermissionByIntegration` could never
//     reach it. Re-keyed to `shopify_store`.
//   · `search_console`'s logo key was `'google_searh_console'` (missing 'c'). Corrected.
// Everything else — including `cm360_advertiser`'s `key: ['cm360']` and the three other rows whose
// `key` array is a short alias rather than the row key — is ported verbatim.
//
// Logo keys are the ONE key space 02's `PlatformLogo` resolves. v3 kept two (imported assets in the
// registry, string keys here); every row that has a provider now shares that provider's `logo`,
// which the completeness spec asserts.

import type { ChannelDisplay } from './providers.types'

export const CHANNEL_DISPLAY: Readonly<Record<string, ChannelDisplay>> = {
  meta_ads_account: {
    key: ['meta_ads_account'],
    nameKey: 'integrations.channels.metaAds',
    logo: 'meta_ads',
    headerKey: 'integrations.channelHeaders.metaAds',
    accountNameFields: ['name'],
  },
  meta_catalog: {
    key: ['meta_catalog'],
    nameKey: 'integrations.channels.metaCatalog',
    logo: 'meta_ads',
    subEntityOf: 'meta_ads_account',
    accountNameFields: ['name'],
  },
  meta_pixel: {
    key: ['meta_pixel'],
    nameKey: 'integrations.channels.metaPixel',
    logo: 'meta_ads',
    subEntityOf: 'meta_ads_account',
    accountNameFields: ['name'],
  },
  // Registered from the Meta modal's Pages step (ORP-22810); the row's `page_id` lives on
  // `integration_config`, which is how the picker leaves an already-connected page out.
  facebook_page: {
    key: ['facebook_page'],
    nameKey: 'integrations.channels.facebookPage',
    logo: 'meta_ads',
    subEntityOf: 'meta_ads_account',
    accountNameFields: ['name'],
  },
  meta_cpas_account: {
    key: ['meta_cpas_account'],
    nameKey: 'integrations.channels.cpasAds',
    logo: 'meta_ads',
  },
  tiktok_ads_account: {
    key: ['tiktok_ads_account'],
    nameKey: 'integrations.channels.tiktok',
    logo: 'tiktok_ads',
    headerKey: 'integrations.channelHeaders.tiktokAds',
    accountNameFields: ['advertiser_name'],
  },
  tiktok_pixel: {
    key: ['tiktok_pixel'],
    nameKey: 'integrations.channels.tiktokPixel',
    logo: 'tiktok_ads',
    subEntityOf: 'tiktok_ads_account',
    accountNameFields: ['pixel_name'],
  },
  tiktok_catalog: {
    key: ['tiktok_catalog'],
    nameKey: 'integrations.channels.tiktokCatalog',
    logo: 'tiktok_ads',
    subEntityOf: 'tiktok_ads_account',
    // Two names because the wire has sent both (`IntegrationsCard.vue:231`).
    accountNameFields: ['tiktok_catalog_name', 'catalog_name'],
  },
  snapchat_account: {
    key: ['snapchat_account'],
    nameKey: 'integrations.channels.snapchat',
    logo: 'snapchat_ads',
  },
  criteo_account: {
    key: ['criteo_account'],
    nameKey: 'integrations.channels.criteo',
    logo: 'criteo',
    headerKey: 'integrations.channelHeaders.criteo',
  },
  rtb_account: {
    key: ['rtb_account'],
    nameKey: 'integrations.channels.rtbHouse',
    logo: 'rtb_house',
    headerKey: 'integrations.channelHeaders.rtbHouse',
  },
  adjust_account: {
    key: ['adjust_account'],
    nameKey: 'integrations.channels.adjust',
    logo: 'adjust',
  },
  yandex_direct_account: {
    key: ['yandex_direct_account'],
    nameKey: 'integrations.channels.yandex',
    logo: 'yandex',
    headerKey: 'integrations.channelHeaders.yandexAds',
  },
  google_ads_account: {
    key: ['google_ads_account'],
    nameKey: 'integrations.channels.googleAds',
    logo: 'google_ads',
    headerKey: 'integrations.channelHeaders.googleAds',
  },
  ga4_analytics_property: {
    key: ['ga4_analytics_property'],
    nameKey: 'integrations.channels.googleAnalytics4',
    logo: 'ga4',
  },
  youtube_channel: {
    key: ['youtube_channel'],
    nameKey: 'integrations.channels.youtube',
    logo: 'youtube',
  },
  search_console: {
    key: ['search_console'],
    nameKey: 'integrations.channels.googleSearchConsole',
    logo: 'google_search_console',
  },
  playstore_account: {
    key: ['playstore_account'],
    nameKey: 'integrations.channels.playStore',
    logo: 'play_store',
  },
  dv360_advertiser: {
    key: ['dv360_advertiser'],
    nameKey: 'integrations.channels.displayVideo360',
    logo: 'dv360',
    headerKey: 'integrations.channelHeaders.dv360',
  },
  cm360_advertiser: {
    key: ['cm360'],
    nameKey: 'integrations.channels.campaignManager360',
    logo: 'cm360',
    headerKey: 'integrations.channelHeaders.cm360',
  },
  merchant_center_account: {
    key: ['merchant_center_account'],
    nameKey: 'integrations.channels.merchantCenter',
    logo: 'merchant_center',
  },
  gtm_container: {
    key: ['gtm_container'],
    nameKey: 'integrations.channels.googleTagManager',
    logo: 'tag_manager',
  },
  bigquery_account: {
    key: ['bigquery_account'],
    nameKey: 'integrations.channels.bigQueryCustom',
    logo: 'bigquery',
  },
  linkedin_ads_account: {
    key: ['linkedin_ads_account'],
    nameKey: 'integrations.channels.linkedin',
    logo: 'linkedin_ads',
    headerKey: 'integrations.channelHeaders.linkedin',
  },
  twitter_ad_account: {
    key: ['twitter_ad_account'],
    nameKey: 'integrations.channels.twitter',
    logo: 'twitter',
    headerKey: 'integrations.channelHeaders.twitter',
  },
  bing_ads_account: {
    key: ['bing_ads_account'],
    nameKey: 'integrations.channels.bing',
    logo: 'bing_ads',
    headerKey: 'integrations.channelHeaders.bingAds',
  },
  reddit_ad_account: {
    key: ['reddit'],
    nameKey: 'integrations.channels.reddit',
    logo: 'reddit',
    headerKey: 'integrations.channelHeaders.reddit',
  },
  pinterest_account: {
    key: ['pinterest'],
    nameKey: 'integrations.channels.pinterest',
    logo: 'pinterest',
    headerKey: 'integrations.channelHeaders.pinterest',
  },
  shopify_store: {
    key: ['shopify_store'],
    nameKey: 'integrations.channels.shopify',
    logo: 'shopify',
  },
  ikas_account: {
    key: ['ikas_account'],
    nameKey: 'integrations.channels.ikas',
    logo: 'ikas',
  },
  ticimax_account: {
    key: ['ticimax_account'],
    nameKey: 'integrations.channels.ticimax',
    logo: 'ticimax',
  },
  tsoft_account: {
    key: ['tsoft_account'],
    nameKey: 'integrations.channels.tSoft',
    logo: 'tsoft',
  },
  app_store: {
    key: ['app_store'],
    nameKey: 'integrations.channels.apple',
    logo: 'apple',
  },
  apple_search_ads_account: {
    key: ['apple_search_ads_account'],
    nameKey: 'integrations.channels.appleSearch',
    logo: 'apple_search_ads',
    headerKey: 'integrations.channelHeaders.appleSearchAds',
  },
  appsflyer_account: {
    key: ['appsflyer_account'],
    nameKey: 'integrations.channels.appsFlyer',
    logo: 'appsflyer',
  },
  singular_account: {
    key: ['singular_account'],
    nameKey: 'integrations.channels.singular',
    logo: 'singular',
  },
  applovin_max_account: {
    key: ['applovin_max_account'],
    nameKey: 'integrations.channels.applovinMax',
    logo: 'applovin_max',
  },
  revenuecat: {
    key: ['revenuecat'],
    nameKey: 'integrations.channels.revenueCat',
    logo: 'revenuecat',
  },
  reklamstore_account: {
    key: ['reklamstore_account'],
    nameKey: 'integrations.channels.reklamStore',
    logo: 'reklamstore',
    headerKey: 'integrations.channelHeaders.reklamstore',
  },
  gelirortaklari_account: {
    key: ['gelirortaklari_account'],
    nameKey: 'integrations.channels.gelirOrtaklari',
    logo: 'gelir_ortaklari',
    headerKey: 'integrations.channelHeaders.gelirortaklari',
  },
  akakce_account: {
    key: ['akakce'],
    nameKey: 'integrations.channels.akakce',
    logo: 'akakce',
    headerKey: 'integrations.channelHeaders.akakce',
  },
  yango_account: {
    key: ['yango_account'],
    nameKey: 'integrations.channels.yango',
    logo: 'yango',
    headerKey: 'integrations.channelHeaders.yango',
  },
  hardal_account: {
    key: ['hardal_account'],
    nameKey: 'integrations.channels.hardal',
    logo: 'hardal',
  },
  openai_ads_account: {
    key: ['openai_ads_account'],
    nameKey: 'integrations.channels.openAi',
    logo: 'openai_ads',
    headerKey: 'integrations.channelHeaders.openAiAds',
  },
}

/**
 * Channel keys that have display metadata but no `IntegrationProvider` — nothing connects them.
 *
 * Five are modal-registered sub-entities (`subEntityOf` names the parent); `meta_cpas_account` is
 * the parentless leftover of a provider v3 deleted. Hence "display-only" rather than "sub-entity" —
 * the fifth member is not one.
 */
export const DISPLAY_ONLY_CHANNEL_KEYS: readonly string[] = [
  'meta_catalog',
  'meta_pixel',
  'facebook_page',
  'meta_cpas_account',
  'tiktok_pixel',
  'tiktok_catalog',
]

const BY_PERMISSION_KEY = new Map<string, ChannelDisplay>()
for (const [channelKey, display] of Object.entries(CHANNEL_DISPLAY)) {
  BY_PERMISSION_KEY.set(channelKey, display)
  // A row key always wins over another row's permission alias: the aliases are the looser space
  // (`cm360`, `reddit`, …) and a collision there must not shadow a real row. The spec asserts the
  // two spaces are in fact disjoint, so this ordering never actually has to arbitrate.
  for (const permissionKey of display.key) {
    if (!BY_PERMISSION_KEY.has(permissionKey)) BY_PERMISSION_KEY.set(permissionKey, display)
  }
}

/**
 * Display metadata for a channel key.
 *
 * Accepts either the row key or any of the row's own permission keys, which is what makes the four
 * rows whose `key` array is a short alias (`cm360`, `reddit`, `pinterest`, `akakce`) reachable from
 * both directions. v3's getter only ever matched the row key.
 */
export function resolveChannelDisplay(channelKey: string): ChannelDisplay | null {
  return BY_PERMISSION_KEY.get(channelKey) ?? null
}
